import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Globe, ChevronRight, Navigation } from 'lucide-react';
import { getBranchBySlug } from '../services/BranchService';
import { normalizeImageUrl, fixHtmlImagePaths, processEmbedUrls } from '../utils/imageUtils';
import { updateMetaTags } from '../utils/seoUtils';
import { getBranchAddressText, getBranchMapUrl } from '../utils/branchUtils';
import useTikTokEmbed from '../utils/useTikTokEmbed';
import AOS from 'aos';
import 'aos/dist/aos.css';

const BranchDetailPage = () => {
    const { slug } = useParams();
    const [branch, setBranch] = useState(null);
    const [loading, setLoading] = useState(true);
    const isFetchingRef = useRef(false);
    const lastFetchedSlugRef = useRef(null);
    const contentRef = useRef(null);

    useTikTokEmbed(contentRef, [branch]);

    useEffect(() => {
        if (branch) {
            updateMetaTags({
                title: branch.name,
                description: branch.address?.street ? `${branch.name} - ${getBranchAddressText(branch.address)}` : branch.name,
                image: branch.imageUrl,
            });
        }
    }, [branch]);

    useEffect(() => {
        AOS.init({ duration: 800, once: true });

        const fetchBranch = async () => {
            if (isFetchingRef.current) return;
            if (lastFetchedSlugRef.current === slug) return;

            isFetchingRef.current = true;
            try {
                const data = await getBranchBySlug(slug);
                setBranch(data);
                lastFetchedSlugRef.current = slug;
            } catch (error) {
                console.error("Error fetching branch details:", error);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        };

        fetchBranch();
        window.scrollTo(0, 0);
    }, [slug]);

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-600"></div>
            </div>
        );
    }

    if (!branch) {
        return (
            <div className="container py-20 text-center">
                <h2 className="text-2xl font-bold text-gray-800">Không tìm thấy chi nhánh</h2>
                <Link to="/gioi-thieu/he-thong-chi-nhanh" className="mt-4 inline-block text-red-600 hover:underline"> Quay lại danh sách chi nhánh</Link>
            </div>
        );
    }

    return (
        <main className="branch-detail-page bg-gray-50">
            <div className="py-4 mt-[50px]">
                <div className="container px-4">
                    <nav aria-label="breadcrumb">
                        <ol className="flex items-center gap-2 text-xs md:text-sm uppercase tracking-wider mb-0 p-0 list-none">
                            <li><Link to="/" className="hover:text-red-600 transition-colors no-underline text-slate-500">Trang chủ</Link></li>
                            <span className="opacity-30">/</span>
                            <li><Link to="/gioi-thieu/he-thong-chi-nhanh" className="hover:text-red-600 transition-colors no-underline text-slate-500">Hệ Thống Chi Nhánh</Link></li>
                            <span className="opacity-30">/</span>
                            <li className="text-slate-900 font-bold line-clamp-1">{branch.name}</li>
                        </ol>
                    </nav>
                </div>
            </div>

            <section className="py-12 lg:py-20">
                <div className="container max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:items-start">

                        <div className="lg:col-span-3 space-y-6 lg:sticky lg:top-28 lg:self-start" data-aos="fade-right">
                            <div className="bg-white p-2 rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                                <div className="aspect-[4/3] rounded-2xl overflow-hidden relative group">
                                    <img
                                        src={normalizeImageUrl(branch.imageUrl)}
                                        alt={branch.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60"></div>
                                    <div className="absolute bottom-4 left-4 text-white">
                                        <span className="bg-black text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mb-1 inline-block shadow-lg">Trung Anh Garage</span>
                                        <h2 className="text-lg font-bold drop-shadow-md leading-tight">{branch.name}</h2>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 space-y-5">
                                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2 border-b pb-3 mb-4">
                                    <Navigation className="text-red-600 w-4 h-4" /> Liên hệ
                                </h3>

                                <div className="flex items-start gap-3 group">
                                    <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0 group-hover:bg-black transition-colors duration-300">
                                        <MapPin className="text-red-600 w-4 h-4 group-hover:text-white transition-colors duration-300" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Địa chỉ</p>
                                        <p className="text-gray-700 text-xs font-medium leading-relaxed">
                                            {getBranchAddressText(branch.address)}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3 group">
                                    <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0 group-hover:bg-black transition-colors duration-300">
                                        <Phone className="text-red-600 w-4 h-4 group-hover:text-white transition-colors duration-300" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Điện thoại</p>
                                        <a href={`tel:${branch.phone}`} className="text-gray-900 font-bold hover:text-red-600 transition-colors text-sm">{branch.phone}</a>
                                    </div>
                                </div>

                                <div className="pt-4">
                                    <a
                                        href={getBranchMapUrl(branch)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-black text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 text-xs shadow-md"
                                    >
                                        <MapPin className="w-4 h-4" /> Chỉ đường
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-9 space-y-10" data-aos="fade-left">
                            <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 lg:p-10 min-h-[600px]">
                                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 leading-tight">
                                    Giới thiệu về {branch.name}
                                </h1>

                                <div
                                    ref={contentRef}
                                    className="prose prose-amber max-w-none text-gray-700 leading-relaxed break-words overflow-hidden w-full ql-content"
                                    dangerouslySetInnerHTML={{ __html: processEmbedUrls(fixHtmlImagePaths(branch.content?.replace(/&nbsp;/g, ' '))) }}
                                />

                                <style>{`
                                    .prose img {
                                        max-width: 100% !important;
                                        height: auto !important;
                                        border-radius: 12px;
                                        margin: 1.5rem 0;
                                    }
                                    .prose iframe {
                                        max-width: 100% !important;
                                        border-radius: 12px;
                                    }
                                    .prose table {
                                        display: block;
                                        overflow-x: auto;
                                        white-space: nowrap;
                                    }
                                    .ql-content * {
                                        word-break: normal !important;
                                        overflow-wrap: break-word !important;
                                        word-wrap: break-word !important;
                                    }
                                `}</style>

                                <div className="mt-12 space-y-6">
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                                        <Clock className="text-red-600 w-5 h-5" /> Vị trí chi nhánh
                                    </h3>
                                    <div className="w-full h-[400px] rounded-2xl overflow-hidden border-4 border-white shadow-2xl relative">
                                        <iframe
                                            title="Chi nhánh map"
                                            width="100%"
                                            height="100%"
                                            frameBorder="0"
                                            style={{ border: 0 }}
                                            src={`https://www.google.com/maps?q=${branch.lat},${branch.lng}&z=15&output=embed`}
                                            allowFullScreen
                                        ></iframe>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default BranchDetailPage;
